import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { validateForm } from "utils/validation";
import { LaundryRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditLaundryForm from "./Componates/EditLaundryForm";
import fields from "../json/LaundryFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import { validateItems } from "../Helper/validateItems";
import {
    Update,
    GetAllProduct,
    GetAllEmployees,
    GetAllTask,
    GetById,
} from "adminApi/LaundryApi/Laundry.js";

/**
 * EditLaundry — smart container for editing an existing laundry record.
 * Fetches existing data by ID, pre-fills the form, and handles update submission.
 */
const EditLaundry = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { formdata, item } = fields;

    // Include "items" in allFields so useFetchFormDataById pre-fills it
    const allFields = useMemo(
        () => [...formdata, { name: "items" }],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // initialValues must match allFields structure
    const initialValues = useMemo(
        () => allFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
        [allFields]
    );

    const { values, errors, handleChange, setValues, setErrors } = useFormChange(
        initialValues,
        LaundryRules
    );

    // Pre-fill form with existing record
    const { loading: loadingFetch } = useFetchFormDataById(id, allFields, GetById, setValues);

    // Dropdown data
    const { data: ProductOptions = [] } = useFetchAllData(GetAllProduct);
    const { data: EmployeesOptions = [] } = useFetchAllData(GetAllEmployees);
    const { data: TaskOptions = [] } = useFetchAllData(GetAllTask);

    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => {
                formData.append(key, key === "items" ? JSON.stringify(val) : (val ?? ""));
            });
            return await Update({ id, data: formData });
        },
        (vals) => {
            const err = validateForm(vals, LaundryRules);
            const itemErrors = validateItems(vals.items);
            if (itemErrors) err.items = itemErrors;
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Laundry updated successfully."
    );

    return (
        <>
            {(loadingSubmit || loadingFetch) && <Spinner isSpinner />}
            <EditLaundryForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                item={item}
                ProductOptions={ProductOptions}
                EmployeesOptions={EmployeesOptions}
                TaskOptions={TaskOptions}
                setValues={setValues}
                validateItems={validateItems}
                setErrors={setErrors}
            />
        </>
    );
};

export default EditLaundry;
